import React from "react";
import { Draggable } from "@hello-pangea/dnd";
import PlatformTemperatureBadge from "./PlatformTemperatureBadge";
import { LEAD_SOURCES, getLeadScoreTier } from "../../constants";
import { getDaysSinceContact, leadNeedsAction } from "../../hooks/usePlatformPipeline";

const getDaysInStage = (stagedAt) => {
    if (!stagedAt) return 0;
    const date = new Date(stagedAt);
    const diff = Date.now() - date.getTime();
    return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
};

const PlatformLeadCard = ({ lead, index, onClick }) => {
    const daysInStage = getDaysInStage(lead.salesData?.stagedAt);
    const sourceLabel = LEAD_SOURCES.find(s => s.key === lead.salesData?.source)?.label || lead.salesData?.source || "Direto";
    const consultantName = lead.salesData?.assignedTo?.name || "Não atribuído";
    const attempts = lead.salesData?.contactAttempts || 0;

    const isArchived = !!lead.salesData?.isArchived;

    const score = lead.salesData?.leadScore;
    const scoreTier = score !== null && score !== undefined ? getLeadScoreTier(score) : null;
    const daysSinceContact = getDaysSinceContact(lead);
    const needsAction = leadNeedsAction(lead);
    const hasAutoMessage = !!lead.salesData?.lastAutoMessageSentAt;

    const cleanPhone = (lead.ownerPhone || lead.phone || "").replace(/\D/g, "");
    const waUrl = cleanPhone ? `https://wa.me/${cleanPhone.startsWith("55") ? cleanPhone : "55" + cleanPhone}` : null;

    return (
        <Draggable draggableId={lead.id} index={index} isDragDisabled={isArchived}>
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    onClick={() => onClick(lead)}
                    className={`card crm-lead-card temp-${lead.salesData?.temperature || "cold"} ${snapshot.isDragging ? "dragging" : ""} ${isArchived ? "is-archived" : ""}`}
                    style={provided.draggableProps.style}
                >
                    <div className="card-body p-3">
                        <div className="d-flex align-items-start justify-content-between mb-2">
                            <h6 className="crm-card-title text-truncate" title={lead.name}>
                                {isArchived && <i className="mdi mdi-archive-outline me-1 text-muted" title="Arquivado" />}
                                {lead.name}
                            </h6>
                            <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                {scoreTier && (
                                    <span
                                        className="badge fw-bold"
                                        style={{ color: scoreTier.color, backgroundColor: scoreTier.bg, fontSize: "10px" }}
                                        title={`Lead Score: ${score}/100 — ${scoreTier.label}. ${scoreTier.description}`}
                                    >
                                        {score}
                                    </span>
                                )}
                                <PlatformTemperatureBadge temperature={lead.salesData?.temperature} />
                            </div>
                        </div>

                        <p className="crm-card-owner">
                            <i className="mdi mdi-account-outline me-1"></i> {lead.ownerName || "Dono não informado"}
                        </p>

                        {needsAction && (
                            <div
                                className="badge bg-soft-danger text-danger d-block text-start mb-2 fw-semibold"
                                style={{ fontSize: "10.5px", whiteSpace: "normal" }}
                                title="Lead ativo sem contato registrado há 3 dias ou mais — priorize o follow-up"
                            >
                                <i className="mdi mdi-bell-alert-outline me-1" />
                                {daysSinceContact}d sem contato — agir agora
                            </div>
                        )}

                        <div className="crm-card-tags">
                            <span className="crm-card-tag" title="Origem do lead">
                                <i className="mdi mdi-compass-outline me-1"></i> {sourceLabel}
                            </span>
                            <span className="crm-card-tag" title="Tempo no estágio atual">
                                <i className="mdi mdi-clock-outline me-1"></i> {daysInStage === 0 ? "Hoje" : `${daysInStage}d no estágio`}
                            </span>
                            {hasAutoMessage && (
                                <span className="crm-card-tag" title={`Cadência automática ativa — última mensagem em ${new Date(lead.salesData.lastAutoMessageSentAt).toLocaleDateString("pt-BR")}`}>
                                    <i className="mdi mdi-robot-outline me-1"></i> Auto
                                </span>
                            )}
                        </div>

                        <div className="crm-card-footer">
                            <div className="crm-card-agent" title="Responsável pelo lead">
                                <i className="mdi mdi-face-agent me-1"></i>
                                <span className="crm-card-agent-name">{consultantName}</span>
                            </div>

                            <div className="d-flex align-items-center gap-1">
                                {waUrl && (
                                    <a
                                        href={waUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="badge bg-soft-success text-success"
                                        title="Chamar no WhatsApp"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <i className="mdi mdi-whatsapp"></i>
                                    </a>
                                )}
                                <div className="badge bg-soft-info text-info crm-card-attempts" title="Tentativas de contato no estágio atual">
                                    <i className="mdi mdi-phone-outgoing me-1"></i>
                                    {attempts} contatos
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Draggable>
    );
};

export default PlatformLeadCard;
